<template>
  <div class="page-content-wrapper data-hub-container">
    <div class="card shadow-sm border rounded-3 mb-4 data-hub-card">
      <div
        class="data-hub-banner p-3 d-flex align-items-center justify-content-between"
      >
        <div class="d-flex align-items-center gap-3">
          <div
            class="hub-icon d-flex align-items-center justify-content-center"
            v-if="current_view === 'profiles'"
          >
            <!-- simple data icon -->
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="3"
                y="3"
                width="7"
                height="7"
                rx="1"
                fill="currentColor"
                opacity="0.9"
              />
              <rect
                x="14"
                y="3"
                width="7"
                height="7"
                rx="1"
                fill="currentColor"
                opacity="0.9"
              />
              <rect
                x="3"
                y="14"
                width="7"
                height="7"
                rx="1"
                fill="currentColor"
                opacity="0.9"
              />
              <rect
                x="14"
                y="14"
                width="7"
                height="7"
                rx="1"
                fill="currentColor"
                opacity="0.9"
              />
            </svg>
          </div>

          <div>
            <h2
              class="mb-0 fs-5 banner-heading"
              v-if="current_view === 'profiles'"
            >
              <span class="banner-title">Data Hub</span>
              <span class="banner-sep"> — </span>
              <span class="banner-subtitle">Data Profile Overview</span>
            </h2>
            <h2 class="mb-0 fs-5 banner-heading" v-else>
              <span class="banner-title">{{
                current_view === "services"
                  ? "Services"
                  : current_view === "visual_mapping"
                  ? "Visual Mapping"
                  : "Customer Applications"
              }}</span>
              <span class="banner-sep"> — </span>
              <span class="banner-subtitle">{{
                selected_entity?.name ||
                selected_entity?.first_name + " " + selected_entity?.last_name
              }}</span>
            </h2>
            <p class="banner-desc mb-0" v-if="current_view === 'profiles'">
              Overview of customer data profiles collected through the workflow
              process.
            </p>
            <p class="banner-desc mb-0" v-else>
              Detailed view of
              {{
                current_view === "services"
                  ? "services"
                  : current_view === "visual_mapping"
                  ? "entity connections"
                  : "customer applications"
              }}
              for the selected entity.
            </p>
          </div>
        </div>
        <button
          v-if="isSystemAdmin && current_view === 'profiles'"
          class="btn btn-bg-secondary text-xs btn-sm rounded-3"
          @click="goToClientAdmins"
        >
          ← Back
        </button>
        <button
          v-if="current_view !== 'profiles'"
          class="btn btn-bg-secondary text-xs btn-sm rounded-3"
          @click="return_to_profiles"
        >
          ← Back
        </button>
      </div>
    </div>

    <!-- Slide Tab Navigation -->
    <div v-if="current_view === 'profiles'" class="slide-tabs-container mb-4">
      <SlideTabComponent
        v-model="active_tab"
        :tabs="data_hub_tabs"
        @tab-changed="handle_tab_change"
      />
    </div>

    <!-- Tables -->
    <div class="tables-wrapper">
      <transition name="fade" mode="out-in">
        <div>
          <!-- Profile Views -->
          <div v-if="current_view === 'profiles'" key="profiles">
            <div v-if="active_tab === 'company'">
              <!-- Company List -->
              <div class="row g-2 mt-2">
                <div class="col-sm-12">
                  <div class="card rounded-3 shadow border">
                    <div class="card-body">
                      <CustomTable
                        :fetchData="fetch_company_data"
                        :columns="companyColumns"
                        :hasActions="true"
                        tableKey="company-table"
                        ref="companyTable"
                      >
                        <template #cell-name="{ row }">
                          <div class="d-flex align-items-center">
                            <div class="avatar-icon avatar-company me-3">
                              <i class="bi bi-building"></i>
                            </div>
                            <div>
                              <div class="fw-bold">{{ row.name }}</div>
                            </div>
                          </div>
                        </template>
                        <template #cell-id="{ row }">
                          <span class="fw-bold text-xs">{{
                            row.identifier
                          }}</span>
                        </template>
                        <template #cell-applications="{ row }">
                          <span class="badge-soft badge-soft-secondary"
                            ><i class="bi bi-ui-checks-grid me-1"></i
                            >{{ row.applications }} applications</span
                          >
                        </template>
                        <template #cell-services="{ row }">
                          <span class="badge-soft badge-soft-secondary"
                            ><i class="bi bi-collection me-1"></i
                            >{{ row.services.length }} services</span
                          >
                        </template>
                        <template #actions="{ row }">
                          <button
                            class="btn btn-sm rounded-3 btn-bg-green me-1"
                            @click="show_customer_applications('company', row)"
                            title="Applications"
                          >
                            <i class="bi bi-kanban me-1"></i>
                            <span class="text-xs fw-semibold"
                              >Applications</span
                            >
                          </button>
                          <button
                            class="btn btn-sm rounded-3 btn-bg-secondary me-1"
                            @click="show_services('company', row)"
                            title="Services"
                          >
                            <i class="bi bi-collection me-1"></i>
                            <span class="text-xs fw-semibold">Services</span>
                          </button>
                          <button
                            class="btn btn-sm rounded-3 btn-bg-info d-none me-1"
                            @click="show_visual_mapping('company', row)"
                            title="Visualize"
                          >
                            <i class="bi bi-diagram-3"></i>
                            <span class="text-xs fw-semibold">Visualize</span>
                          </button>
                        </template>
                      </CustomTable>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="active_tab === 'individual'">
              <!-- Individual List -->
              <div class="row g-2 mt-2">
                <div class="col-sm-12">
                  <div class="card rounded-3 shadow border">
                    <div class="card-body">
                      <CustomTable
                        :fetchData="fetch_individual_data"
                        :columns="individualColumns"
                        :hasActions="true"
                        tableKey="individual-table"
                        ref="individualTable"
                      >
                        <template #cell-first_name="{ row }">
                          <div class="d-flex align-items-center">
                            <div class="avatar-icon avatar-individual me-3">
                              <i class="bi bi-person"></i>
                            </div>
                            <div>
                              <div class="fw-bold">
                                {{ row.first_name }} {{ row.last_name }}
                              </div>
                              <small class="text-muted">Individual</small>
                            </div>
                          </div>
                        </template>
                        <template #cell-id="{ row }">
                          <span class="fw-bold text-xs">{{
                            row.identifier
                          }}</span>
                        </template>
                        <template #cell-applications="{ row }">
                          <span class="badge-soft badge-soft-secondary"
                            ><i class="bi bi-ui-checks-grid me-1"></i
                            >{{ row.applications }} applications</span
                          >
                        </template>
                        <template #cell-services="{ row }">
                          <span class="badge-soft badge-soft-secondary"
                            ><i class="bi bi-collection me-1"></i
                            >{{ row.services.length }} services</span
                          >
                        </template>
                        <template #actions="{ row }">
                          <button
                            class="btn btn-sm rounded-3 btn-bg-green me-1"
                            @click="
                              show_customer_applications('individual', row)
                            "
                            title="Applications"
                          >
                            <i class="bi bi-kanban me-1"></i>
                            <span class="text-xs fw-semibold"
                              >Applications</span
                            >
                          </button>
                          <button
                            class="btn btn-sm rounded-3 btn-bg-secondary me-1"
                            @click="show_services('individual', row)"
                            title="Services"
                          >
                            <i class="bi bi-collection me-1"></i>
                            <span class="text-xs fw-semibold">Services</span>
                          </button>
                          <button
                            class="btn btn-sm rounded-3 btn-bg-info d-none me-1"
                            @click="show_visual_mapping('individual', row)"
                            title="Visualize"
                          >
                            <i class="bi bi-diagram-3 me-1"></i>
                            <span class="text-xs fw-semibold">Visualize</span>
                          </button>
                          <button
                            class="btn btn btn-bg-info text-xs btn-sm rounded-3 me-1"
                            @click="show_entity_associations(row)"
                          >
                            <i class="bi bi-person-plus me-1"></i>
                            <span class="text-xs fw-semibold">Associate Company</span>
                          </button>
                        </template>
                      </CustomTable>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Customer Applications View -->
          <div
            v-if="current_view === 'customer_applications'"
            key="customer_applications"
          >
            <CustomerApplications
              :entity="selected_entity"
              :entity-type="selected_entity_type"
            />
          </div>

          <!-- Services View -->
          <div v-else-if="current_view === 'services'" key="services">
            <ServicesAction
              :entity="selected_entity"
              :entity-type="selected_entity_type"
            />
          </div>

          <!-- Visual Mapping View -->
          <div
            v-else-if="current_view === 'visual_mapping'"
            key="visual_mapping"
          >
            <VisualMapping
              :entity="selected_entity"
              :entity-type="selected_entity_type"
            />
          </div>
        </div>
      </transition>
    </div>

    <!-- Entity Assignment Modal -->
    <div
      class="modal fade"
      id="entity_assignment_modal"
      tabindex="-1"
      aria-labelledby="entity_assignment_modal_label"
      aria-hidden="true"
      ref="assignment_modal"
    >
      <div class="modal-dialog modal-xl modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5
              class="modal-title fs-6 fw-bold"
              id="entity_assignment_modal_label"
            >
              Assign Customer Applications -
              {{ selected_individual?.first_name }}
              {{ selected_individual?.last_name }}
            </h5>
          </div>
          <div class="modal-body">
            <!-- Search Fields -->
            <div class="row mb-2">
              <div class="col-md-12 mb-2">
                <label
                  for="company_name_search"
                  class="form-label text-xs text-muted fw-semibold"
                  >Company Name</label
                >
                <input
                  type="text"
                  class="form-control"
                  id="company_name_search"
                  v-model="association_search.company_name"
                  @input="handle_association_search"
                  placeholder="Search by company name..."
                />
              </div>
              <div class="col-md-12">
                <label
                  for="company_registration_search"
                  class="form-label text-xs text-muted fw-semibold"
                  >Company Registration</label
                >
                <input
                  type="text"
                  class="form-control"
                  id="company_registration_search"
                  v-model="association_search.company_registration"
                  @input="handle_association_search"
                  placeholder="Search by registration number..."
                />
              </div>
            </div>

            <!-- Companies Table -->
            <div class="table-responsive">
              <CustomTable
                :fetchData="fetch_company_data"
                :columns="association_company_columns"
                tableKey="association_companies"
                :hasActions="true"
                ref="association_table"
              >
                <!-- Company Name Cell -->
                <template #cell-name="{ row }">
                  <div class="company-row">
                    <div class="initial-block" :title="row.name">
                      {{ (row.name || "").charAt(0).toUpperCase() }}
                    </div>
                    <div class="company-info">
                      <div class="company-name">{{ row.name }}</div>
                      <div class="company-meta">
                        {{ row.registration_number }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Assignment Action -->
                <template #actions="{ row }">
                  <button
                    class="btn btn-bg-green text-xs btn-sm rounded-3"
                    @click="assign_customer_applications(row)"
                    :disabled="
                      loading_association ||
                      row.customer_applications_count === 0
                    "
                    :title="
                      row.customer_applications_count === 0
                        ? 'No applications to assign'
                        : `Assign application(s)`
                    "
                  >
                    <i
                      v-if="loading_association"
                      class="fas fa-spinner fa-spin me-1"
                    ></i>
                    Assign ({{ row.customer_applications_count || 0 }})
                  </button>
                </template>
              </CustomTable>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-bg-secondary text-xs rounded-3"
              data-bs-dismiss="modal"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SlideTabComponent from "@/components/SlideTab/SlideTabComponent.vue";
import CustomTable from "@/components/CustomTable/CustomTable.vue";
import CustomerApplications from "./components/customer_applications/CustomerApplications.vue";
import ServicesAction from "./components/services/Services.vue";
import VisualMapping from "./components/tree/VisualMapping.vue";
import { API_BASE_URL } from "@/config";
import Swal from "sweetalert2";

export default {
  name: "DataHub",
  props: {
    clientAdminId: {
      type: [String, Number],
      default: null,
    },
  },
  components: {
    SlideTabComponent,
    CustomTable,
    CustomerApplications,
    ServicesAction,
    VisualMapping,
  },
  data() {
    return {
      active_tab: "company",
      current_view: "profiles",
      selected_entity: null,
      selected_entity_type: null,
      loading_entities: false,
      entities_error: null,
      data_hub_tabs: [
        { label: "Company Profiles", value: "company" },
        { label: "Individual Profiles", value: "individual" },
      ],

      // Columns definitions for CustomTable
      companyColumns: [
        { key: "name", label: "Name" },
        { key: "id", label: "Registration" },
        { key: "applications", label: "Applications" },
        { key: "services", label: "Services" },
      ],

      individualColumns: [
        { key: "first_name", label: "Name" },
        { key: "id", label: "ID" },
        { key: "applications", label: "Applications" },
        { key: "services", label: "Services" },
      ],

      individual: {
        search_input: "",
        select_service: "",
        select_workflow: "",
      },
      company: {
        search_input: "",
        select_service: "",
        select_workflow: "",
      },

      company_fixtures: [],
      individual_fixtures: [],
      last_updated: new Date().toLocaleString(),

      // Association modal data
      selected_individual: null,
      association_search: {
        company_name: "",
        company_registration: "",
      },
      association_search_debounce: null,
      loading_association: false,
      existing_associations: [],
      association_company_columns: [
        { key: "name", label: "Company Name" },
        { key: "registration_number", label: "Registration Number" },
        { key: "customer_applications_count", label: "Applications" },
      ],
    };
  },
  computed: {
    company_count() {
      return this.company_fixtures ? this.company_fixtures.length : 0;
    },
    individual_count() {
      return this.individual_fixtures ? this.individual_fixtures.length : 0;
    },
    isSystemAdmin() {
      return localStorage.getItem("user_role").toLowerCase() === "systemadmin";
    },
  },
  methods: {
    handle_tab_change(value) {
      this.active_tab = value;
      // Reset view to profiles when switching tabs
      this.current_view = "profiles";
      this.selected_entity = null;
      this.selected_entity_type = null;
    },

    show_customer_applications(entity_type, entity) {
      this.current_view = "customer_applications";
      this.selected_entity = entity;
      this.selected_entity_type = entity_type;
    },

    show_services(entity_type, entity) {
      this.current_view = "services";
      this.selected_entity = entity;
      this.selected_entity_type = entity_type;
    },

    show_visual_mapping(entity_type, entity) {
      this.current_view = "visual_mapping";
      this.selected_entity = entity;
      this.selected_entity_type = entity_type;
    },

    return_to_profiles() {
      this.current_view = "profiles";
      this.selected_entity = null;
      this.selected_entity_type = null;
    },

    reset_filters() {
      if (this.active_tab === "company") {
        this.company.search_input = "";
        this.company.select_service = "";
        this.company.select_workflow = "";
      } else {
        this.individual.search_input = "";
        this.individual.select_service = "";
        this.individual.select_workflow = "";
      }
    },
    fetch_company_data(page = 1, search = "") {
      const TOKEN = localStorage.getItem("token");
      let url = `${API_BASE_URL}/verify_api/data_hub/entities/?page=${page}&group_type=enterprise`;
      if (this.clientAdminId) {
        url += `&client_admin_id=${encodeURIComponent(this.clientAdminId)}`;
      }
      if (this.company.search_input || search) {
        url += `&search=${encodeURIComponent(this.company.search_input || search)}`;
      }
      return new Promise((resolve, reject) => {
        $.ajax({
          url: url,
          method: "GET",
          dataType: "json",
          headers: {
            Authorization: `Token ${TOKEN}`,
          },
          success: (data) => {
            const results = (data.results || []).map((e, idx) => {
              const info = e.entity_information || {};
              return {
                id: e.id ?? "",
                identifier: e.identifier || info.registration_number || "",
                name:
                  info.company_name ||
                  info.name ||
                  e.name ||
                  `Enterprise ${idx + 1}`,
                applications: e.customer_applications?.length || 0,
                services: e.services || [],
                workflows: e.workflows || [],
                registration_number: e.identifier || info.registration_number || "",
                customer_applications_count: e.customer_applications_count || 0,
                raw: e,
              };
            });
            resolve({ results, count: data.count || results.length });
          },
          error: (xhr, status, error) => {
            reject(error);
          },
        });
      });
    },

    fetch_individual_data(page = 1, search = "") {
      const TOKEN = localStorage.getItem("token");
      let url = `${API_BASE_URL}/verify_api/data_hub/entities/?page=${page}&group_type=individual`;
      if (this.clientAdminId) {
        url += `&client_admin_id=${encodeURIComponent(this.clientAdminId)}`;
      }
      if (this.individual.search_input || search) {
        url += `&search=${encodeURIComponent(this.individual.search_input || search)}`;
      }
      return new Promise((resolve, reject) => {
        $.ajax({
          url: url,
          method: "GET",
          dataType: "json",
          headers: {
            Authorization: `Token ${TOKEN}`,
          },
          success: (data) => {
            const results = (data.results || []).map((p) => {
              const info = p.entity_information || {};
              return {
                id: p.id ?? "",
                identifier: p.identifier || info.id_number || "",
                first_name: info.first_name || p.first_name || "",
                last_name: info.last_name || p.last_name || "",
                applications: p.customer_applications?.length || 0,
                services: p.services || [],
                workflows: p.workflows || [],
                id_number: p.identifier || info.id_number || "",
                customer_applications_count: p.customer_applications_count || 0,
                raw: p,
              };
            });
            resolve({ results, count: data.count || results.length });
          },
          error: (xhr, status, error) => {
            reject(error);
          },
        });
      });
    },

    async fetch_entities() {
      this.last_updated = new Date().toLocaleString();
      this.$nextTick(() => {
        if (this.$refs.companyTable && this.active_tab === "company") {
          this.$refs.companyTable.loadData();
        }
        if (this.$refs.individualTable && this.active_tab === "individual") {
          this.$refs.individualTable.loadData();
        }
      });
    },
    goToClientAdmins() {
      this.$router.push({ name: 'client_admins' });
    },

    // Assignment methods
    show_entity_associations(individual_entity) {
      this.selected_individual = individual_entity;
      this.association_search.company_name = "";
      this.association_search.company_registration = "";
      this.existing_associations = [];

      // Show the modal
      $(this.$refs.assignment_modal).modal("show");

      // Load existing associations (now just refreshes the table)
      this.load_existing_associations();
    },

    async load_existing_associations() {
      if (!this.selected_individual) return;

      // Since we no longer store associations, just refresh the table
      this.$nextTick(() => {
        if (this.$refs.association_table) {
          this.$refs.association_table.loadData();
        }
      });
    },

    handle_association_search() {
      clearTimeout(this.association_search_debounce);
      this.association_search_debounce = setTimeout(() => {
        if (this.$refs.association_table) {
          this.$refs.association_table.meta.page = 1;
          this.$refs.association_table.loadData();
        }
      }, 500);
    },

    fetch_companies_for_association(page = 1) {
      const TOKEN = localStorage.getItem("token");
      let url = `${API_BASE_URL}/verify_api/data_hub/entities/?page=${page}&group_type=enterprise`;
      if (this.clientAdminId) {
        url += `&client_admin_id=${encodeURIComponent(this.clientAdminId)}`;
      }
      // Combine association searches into a single search query
      const search_terms = [];
      if (this.association_search.company_name.trim()) {
        search_terms.push(this.association_search.company_name.trim());
      }
      if (this.association_search.company_registration.trim()) {
        search_terms.push(this.association_search.company_registration.trim());
      }
      if (search_terms.length > 0) {
        url += `&search=${encodeURIComponent(search_terms.join(' '))}`;
      }
      return new Promise((resolve, reject) => {
        $.ajax({
          url: url,
          method: "GET",
          dataType: "json",
          headers: {
            Authorization: `Token ${TOKEN}`,
          },
          success: (data) => {
            const results = (data.results || []).map((e, idx) => {
              const info = e.entity_information || {};
              return {
                id: e.id || "",
                name:
                  info.company_name ||
                  info.name ||
                  e.name ||
                  `Enterprise ${idx + 1}`,
                registration_number: e.identifier || info.registration_number || "",
                customer_applications_count: e.customer_applications_count || 0,
                raw: e,
              };
            });
            resolve({ results, count: data.count || results.length });
          },
          error: (xhr, status, error) => {
            reject(error);
          },
        });
      });
    },

    async assign_customer_applications(company_entity) {
      if (!this.selected_individual) return;

      this.loading_association = true;

      try {
        const TOKEN = localStorage.getItem("token");
        if (!TOKEN) {
          throw new Error("Missing auth token");
        }

        const confirm_result = await Swal.fire({
          icon: "question",
          title: "Confirm Assignment",
          text: `Are you sure you want to assign customer application(s) from "${this.selected_individual.first_name} ${this.selected_individual.last_name}" to "${company_entity.name}"?`,
          showCancelButton: true,
          confirmButtonText: "Yes, Assign",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#0ea5a9",
          cancelButtonColor: "#6c757d",
        });

        if (!confirm_result.isConfirmed) {
          return;
        }

        const url = `${API_BASE_URL}/verify_api/data_hub/entities/associations/create/`;

        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${TOKEN}`,
          },
          body: JSON.stringify({
            parent_entity_id: company_entity.id,
            child_entity_id: this.selected_individual.id,
          }),
        });

        if (!response.ok) {
          const error_data = await response.json().catch(() => ({}));
          throw new Error(
            error_data.message ||
              `Failed to assign applications (${response.status})`
          );
        }

        const result = await response.json();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text:
            result.message || "Customer applications assigned successfully!",
          confirmButtonText: "OK",
          confirmButtonColor: "#0ea5a9",
        });

        // Refresh entities data
        await this.fetch_entities();

        // Close modal
        $(this.$refs.assignment_modal).modal("hide");
      } catch (err) {
        console.error("Failed to assign applications", err);
        Swal.fire({
          icon: "error",
          title: "Assignment Failed",
          text:
            err.message ||
            "Failed to assign customer applications. Please try again.",
          confirmButtonText: "OK",
          confirmButtonColor: "#dc3545",
        });
      } finally {
        this.loading_association = false;
      }
    },
  },
  watch: {
    clientAdminId() {
      this.fetch_entities();
    },
    "company.search_input"() {
      this.$nextTick(() => {
        if (this.$refs.companyTable) this.$refs.companyTable.loadData();
      });
    },
    "company.select_service"() {
      this.$nextTick(() => {
        if (this.$refs.companyTable) this.$refs.companyTable.loadData();
      });
    },
    "company.select_workflow"() {
      this.$nextTick(() => {
        if (this.$refs.companyTable) this.$refs.companyTable.loadData();
      });
    },
    "individual.search_input"() {
      this.$nextTick(() => {
        if (this.$refs.individualTable) this.$refs.individualTable.loadData();
      });
    },
    "individual.select_service"() {
      this.$nextTick(() => {
        if (this.$refs.individualTable) this.$refs.individualTable.loadData();
      });
    },
    "individual.select_workflow"() {
      this.$nextTick(() => {
        if (this.$refs.individualTable) this.$refs.individualTable.loadData();
      });
    },
  },
  mounted() {
    this.fetch_entities();
  },
};
</script>

<style scoped>
.tables-wrapper {
  min-height: 200px;
}

.cell-content {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 260px;
}

.cell-small {
  font-size: 0.8rem;
  color: #6c757d;
}

.d-flex.gap-2 {
  gap: 0.5rem;
}

.fw-semibold {
  font-weight: 600;
}

/* Data hub header styles */
.data-hub-card {
  overflow: hidden;
}

.data-hub-banner {
  background: linear-gradient(90deg, #1e3a8a, #0ea5a9);
  color: #fff;
}

.hub-icon {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: #f1f3f5;
  border: 1px solid rgba(0, 0, 0, 0.04);
}

.stats .stat-value {
  font-weight: 700;
  font-size: 1.05rem;
}

.stats .stat-label {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.85);
}

.chip {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background: #f1f3f5;
  border-radius: 999px;
  font-size: 0.8rem;
  color: #495057;
}

.text-white-50 {
  color: rgba(255, 255, 255, 0.7);
}

/* Banner: switch from gradient to white with subtle shadow and readable text */
.data-hub-banner {
  background: #fff;
  color: #212529;
  box-shadow: 0 8px 20px rgba(16, 24, 40, 0.06);
}

/* The template uses utility classes like .text-white; override them inside the banner so text remains visible on white */
.data-hub-banner .text-white {
  color: #212529 !important;
}

.data-hub-banner .text-white-50 {
  color: rgba(0, 0, 0, 0.6) !important;
}

/* Banner heading tweaks: combine main title and smaller subtitle */
.banner-heading {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.banner-title {
  font-weight: 700;
  color: #111827;
}

.banner-sep {
  color: rgba(0, 0, 0, 0.45);
}

.banner-subtitle {
  font-weight: 600;
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.65);
}

.banner-desc {
  font-size: 0.75rem;
  line-height: 1.2;
  color: rgba(0, 0, 0, 0.55);
  margin-top: 2px;
}

/* Table row improvements */
.company-name,
.person-name {
  font-size: 0.95rem;
  font-weight: 600;
  color: #0f172a;
}

.company-meta,
.person-meta {
  font-size: 0.78rem;
  color: #6b7280;
  margin-top: 2px;
}

/* Company row: initial block + info */
.company-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.initial-block {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: var(--primary-color, #0ea5a9);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
  flex: 0 0 40px;
  box-shadow: 0 1px 0 rgba(16, 24, 40, 0.03) inset;
}

.company-info {
  display: flex;
  flex-direction: column;
}

/* Utility: accessible hide */
@media (max-width: 767px) {
  .data-hub-banner {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }

  .stats {
    justify-content: flex-end;
  }
}

/* Clean header: remove default grey background and tighten spacing */
.sub-card-header {
  background: transparent !important;
  border-bottom: none !important;
  padding-top: 0.6rem !important;
  padding-bottom: 0.6rem !important;
}

.sub-card-header h5 {
  margin: 0;
  font-size: 1rem;
}

.sub-card-header small {
  font-size: 0.8rem;
  color: #6b7280;
}

/* Transition animations */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}

/* Loading & error states */
.entities-loading,
.entities-error {
  font-size: 0.8rem;
  margin-top: 4px;
}

.entities-error {
  color: #b91c1c;
}

.avatar-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-icon i {
  font-size: 20px;
}

.avatar-individual {
  background: rgba(13, 110, 253, 0.08);
}

.avatar-company {
  background: rgba(25, 135, 84, 0.08);
}

/* Badges */
.badge-soft {
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
}
.badge-soft-primary {
  background: rgba(13, 110, 253, 0.1);
  color: var(--bs-secondary);
}
.badge-soft-success {
  background: rgba(25, 135, 84, 0.1);
  color: var(--bs-success);
}
.badge-soft-secondary {
  background: rgba(108, 117, 125, 0.1);
  color: #6c757d;
}
</style>
